/**
 * Landing Page Content Editor — super_admin only.
 * Lets the platform owner tweak marketing copy, stats, beta partners, pain
 * points, and CTA text without touching code or asking the agent.
 * Saves to `platform_landing_content` via PUT /api/platform/landing-content.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Save, RotateCcw, ExternalLink, Plus, Trash2, Loader2, MapPin, TrendingDown, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PAIN_ICONS = ['shuffle', 'clock', 'alert-circle', 'database', 'trending-down', 'puzzle', 'dollar-sign', 'alert-triangle'];

export const LandingContentEditorPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/platform/landing-content`);
      setContent(data);
    } catch (e) {
      toast.error('Failed to load landing content');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/platform/landing-content`, content);
      setContent(data);
      toast.success('Landing page updated — refresh the public page to see changes');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Reset landing page to code defaults? Your custom copy will be lost.')) return;
    setResetting(true);
    try {
      const { data } = await axios.post(`${API}/api/platform/landing-content/reset`);
      setContent(data.content);
      toast.success('Landing content reset to defaults');
    } catch (e) {
      toast.error('Reset failed');
    } finally {
      setResetting(false);
    }
  };

  // Deep-set helper — updates a nested field via dot path
  const setField = (path, value) => {
    setContent((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = clone;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] ?? {};
      cur[parts[parts.length - 1]] = value;
      return clone;
    });
  };

  const updateArrayItem = (key, index, field, value) => {
    setContent((prev) => {
      const clone = JSON.parse(JSON.stringify(prev));
      clone[key][index][field] = value;
      return clone;
    });
  };

  const addArrayItem = (key, template) => {
    setContent((prev) => ({ ...prev, [key]: [...(prev[key] || []), template] }));
  };

  const removeArrayItem = (key, index) => {
    setContent((prev) => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }));
  };

  if (loading || !content) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="landing-editor-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-emerald-brand" />
            Landing Page Editor
          </h1>
          <p className="text-sm text-gray-medium mt-1">
            Edit any copy or stat on the marketing landing page. Changes go live instantly.
            {content.updated_at && (
              <span className="ml-2 text-xs">
                (Last edit: {new Date(content.updated_at).toLocaleString()} by {content.updated_by})
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={() => window.open('/', '_blank')} data-testid="preview-landing-btn">
            <ExternalLink className="h-4 w-4 mr-2" />Preview
          </Button>
          <Button variant="outline" size="sm" onClick={resetToDefaults} disabled={resetting} data-testid="reset-defaults-btn">
            {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
            Reset
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-brand hover:bg-emerald-hover" data-testid="save-landing-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="hero" className="w-full">
        <TabsList className="grid grid-cols-3 sm:grid-cols-6">
          <TabsTrigger value="hero" data-testid="tab-hero">Hero</TabsTrigger>
          <TabsTrigger value="roi" data-testid="tab-roi">Live ROI</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">Stats</TabsTrigger>
          <TabsTrigger value="beta" data-testid="tab-beta">Beta</TabsTrigger>
          <TabsTrigger value="pain" data-testid="tab-pain">Pain Points</TabsTrigger>
          <TabsTrigger value="cta" data-testid="tab-cta">Final CTA</TabsTrigger>
        </TabsList>

        {/* HERO TAB */}
        <TabsContent value="hero" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Hero Section</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tagline (green text under the K logo)</Label>
                <Input value={content.hero.tagline || ''} onChange={(e) => setField('hero.tagline', e.target.value)} data-testid="edit-tagline" placeholder="Connecting Pieces Of The Business Puzzle" />
              </div>
              <div>
                <Label>Beta Badge Text (top pill)</Label>
                <Input value={content.hero.badge_text} onChange={(e) => setField('hero.badge_text', e.target.value)} data-testid="edit-badge" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Title Line 1 (black)</Label>
                  <Input value={content.hero.title_line_1} onChange={(e) => setField('hero.title_line_1', e.target.value)} data-testid="edit-title-1" />
                </div>
                <div>
                  <Label>Title Line 2 (green)</Label>
                  <Input value={content.hero.title_line_2} onChange={(e) => setField('hero.title_line_2', e.target.value)} data-testid="edit-title-2" />
                </div>
              </div>
              <div>
                <Label>KONNECT Acronym Tagline (sits below title)</Label>
                <Textarea rows={2} value={content.hero.acronym_tagline} onChange={(e) => setField('hero.acronym_tagline', e.target.value)} data-testid="edit-acronym" />
                <p className="text-xs text-gray-400 mt-1">Separate items with · . The first letter of each phrase is auto-bolded.</p>
              </div>
              <div>
                <Label>Subtitle Paragraph</Label>
                <Textarea rows={3} value={content.hero.subtitle} onChange={(e) => setField('hero.subtitle', e.target.value)} data-testid="edit-subtitle" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Primary CTA Button</Label>
                  <Input value={content.hero.primary_cta} onChange={(e) => setField('hero.primary_cta', e.target.value)} data-testid="edit-cta-primary" />
                </div>
                <div>
                  <Label>Secondary CTA Button</Label>
                  <Input value={content.hero.secondary_cta} onChange={(e) => setField('hero.secondary_cta', e.target.value)} data-testid="edit-cta-secondary" />
                </div>
              </div>
              <div>
                <Label>Trust Line (small text below buttons)</Label>
                <Input value={content.hero.trust_line} onChange={(e) => setField('hero.trust_line', e.target.value)} data-testid="edit-trust-line" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LIVE ROI TAB */}
        <TabsContent value="roi" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Live ROI Widget (below hero)</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Hours Saved / Month (counter target)</Label>
                <Input type="number" value={content.live_roi.hours_saved_per_month} onChange={(e) => setField('live_roi.hours_saved_per_month', Number(e.target.value))} data-testid="edit-roi-hours" />
              </div>
              <div>
                <Label>Productivity Value in USD (counter target)</Label>
                <Input type="number" value={content.live_roi.value_created_usd} onChange={(e) => setField('live_roi.value_created_usd', Number(e.target.value))} data-testid="edit-roi-value" />
              </div>
              <div>
                <Label>Tasks Automated (counter target)</Label>
                <Input type="number" value={content.live_roi.tasks_automated} onChange={(e) => setField('live_roi.tasks_automated', Number(e.target.value))} data-testid="edit-roi-tasks" />
              </div>
              <div>
                <Label>Modules Active (out of 89)</Label>
                <Input type="number" value={content.live_roi.modules_active} onChange={(e) => setField('live_roi.modules_active', Number(e.target.value))} data-testid="edit-roi-modules" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* STATS TAB */}
        <TabsContent value="stats" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Dark Stats Strip (4 pillars)</CardTitle>
              <Button size="sm" variant="outline" onClick={() => addArrayItem('stats_strip', { value: '0', label: 'New Stat', sublabel: '' })} data-testid="add-stat-btn">
                <Plus className="h-4 w-4 mr-1" />Add
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {content.stats_strip.map((stat, i) => (
                <div key={i} className="flex gap-2 items-end border-b pb-3 last:border-b-0" data-testid={`stat-row-${i}`}>
                  <div className="w-24">
                    <Label className="text-xs">Value</Label>
                    <Input value={stat.value} onChange={(e) => updateArrayItem('stats_strip', i, 'value', e.target.value)} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Label</Label>
                    <Input value={stat.label} onChange={(e) => updateArrayItem('stats_strip', i, 'label', e.target.value)} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Sublabel (optional)</Label>
                    <Input value={stat.sublabel || ''} onChange={(e) => updateArrayItem('stats_strip', i, 'sublabel', e.target.value)} />
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeArrayItem('stats_strip', i)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BETA PARTNERS TAB */}
        <TabsContent value="beta" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" />Beta Partners Strip</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Headline (uppercase small text)</Label>
                <Input value={content.beta_partners_headline} onChange={(e) => setField('beta_partners_headline', e.target.value)} data-testid="edit-beta-headline" />
              </div>
              <div>
                <Label>Subline Paragraph</Label>
                <Textarea rows={3} value={content.beta_partners_subline} onChange={(e) => setField('beta_partners_subline', e.target.value)} data-testid="edit-beta-subline" />
              </div>
              <div>
                <Label>Beta Regions (chips)</Label>
                <div className="space-y-2 mt-2">
                  {content.beta_regions.map((r, i) => (
                    <div key={i} className="flex gap-2" data-testid={`beta-region-row-${i}`}>
                      <Input value={r} onChange={(e) => {
                        const arr = [...content.beta_regions];
                        arr[i] = e.target.value;
                        setField('beta_regions', arr);
                      }} />
                      <Button size="icon" variant="ghost" onClick={() => setField('beta_regions', content.beta_regions.filter((_, x) => x !== i))}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setField('beta_regions', [...content.beta_regions, 'New Region'])} data-testid="add-region-btn">
                    <Plus className="h-4 w-4 mr-1" />Add Region
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAIN POINTS TAB */}
        <TabsContent value="pain" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><TrendingDown className="h-4 w-4" />Hidden-Cost Pain Points</CardTitle>
              <Button size="sm" variant="outline" onClick={() => addArrayItem('pain_points', { icon: 'alert-circle', title: 'New pain point', desc: 'Description...', impact_stat: 'Impact figure' })} data-testid="add-pain-btn">
                <Plus className="h-4 w-4 mr-1" />Add
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {content.pain_points.map((p, i) => (
                <div key={i} className="border rounded-lg p-3 space-y-2" data-testid={`pain-row-${i}`}>
                  <div className="flex justify-between items-center">
                    <Badge variant="outline">#{i + 1}</Badge>
                    <Button size="icon" variant="ghost" onClick={() => removeArrayItem('pain_points', i)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Icon</Label>
                      <select className="w-full h-9 border rounded px-2 text-sm" value={p.icon} onChange={(e) => updateArrayItem('pain_points', i, 'icon', e.target.value)}>
                        {PAIN_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Impact Stat</Label>
                      <Input value={p.impact_stat || ''} onChange={(e) => updateArrayItem('pain_points', i, 'impact_stat', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Title</Label>
                    <Input value={p.title} onChange={(e) => updateArrayItem('pain_points', i, 'title', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Textarea rows={3} value={p.desc} onChange={(e) => updateArrayItem('pain_points', i, 'desc', e.target.value)} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FINAL CTA TAB */}
        <TabsContent value="cta" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Final Call-to-Action</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Headline</Label>
                <Input value={content.cta_final_headline} onChange={(e) => setField('cta_final_headline', e.target.value)} data-testid="edit-cta-headline" />
              </div>
              <div>
                <Label>Subline</Label>
                <Textarea rows={2} value={content.cta_final_subline} onChange={(e) => setField('cta_final_subline', e.target.value)} data-testid="edit-cta-subline" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LandingContentEditorPage;
